// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import { Validator } from '@cfworker/json-schema';
import { distance } from 'fastest-levenshtein';

import type { CarriedAssertion, Config, Row, ScoreValue, ScorerResult } from '../types';
import { LIMITS } from '../types';
import { classifyJson, shapePasses } from './jsonshape';

/**
 * Every scorer had to pass one test to be here: CAN IT HONESTLY SAY "THIS GOT WORSE"?
 *
 * Five candidates failed it. Bare edit distance and bare word count can only say "this changed" —
 * a distance of 400 is not worse than a distance of 3 — so they are Class B, "changed, not judged",
 * and are structurally incapable of producing a verdict. ROUGE-L against a reference was cut
 * entirely: measured on a harmless paraphrase it drops 0.50, five times any sensible threshold, so
 * it flags precisely the thing this tool exists not to flag. Tool-call argument F1 was cut because
 * F1 needs a reference and we have two real runs and no reference. Cosine similarity was cut
 * because it needs a model and it cannot say "worse" either.
 */

export interface ScorerDef {
  readonly id: string;
  readonly label: string;
  readonly klass: 'verdict' | 'change';
  readonly describe: string;
  /** Why this scorer might not be able to look at a given file. Null when it always can. */
  readonly requires: string | null;
}

export const SCORERS: readonly ScorerDef[] = [
  {
    id: 'errored',
    label: 'errored',
    klass: 'verdict',
    describe: 'The call itself failed, returned nothing, or was cut off at the token limit.',
    requires: null,
  },
  {
    id: 'carried-assertion',
    label: 'your own assertions',
    klass: 'verdict',
    describe: 'An assertion your eval harness recorded as passing in A is recorded as failing in B.',
    requires: 'assertion results in the file',
  },
  {
    id: 'is-json',
    label: 'valid JSON',
    klass: 'verdict',
    describe: 'The output parsed as JSON in A and does not in B.',
    requires: 'output that was JSON in at least one run',
  },
  {
    id: 'json-schema',
    label: 'schema',
    klass: 'verdict',
    describe: 'Both outputs parse, A satisfies your schema and B does not.',
    requires: 'a JSON Schema you paste in',
  },
  {
    id: 'expected-match',
    label: 'expected value',
    klass: 'verdict',
    describe: 'The output matched the expected column in A and does not in B.',
    requires: 'an expected / reference column',
  },
  {
    id: 'length-bounds',
    label: 'length bounds',
    klass: 'verdict',
    describe: 'The output was inside your length bounds in A and is outside them in B.',
    requires: 'bounds you set',
  },
  {
    id: 'latency-regression',
    label: 'latency',
    klass: 'verdict',
    describe: 'B is both more than the ratio slower and more than the floor slower.',
    requires: 'a non-zero latency in both files',
  },
  {
    id: 'cost-regression',
    label: 'cost',
    klass: 'verdict',
    describe: 'B costs both more than the ratio more and more than the floor more.',
    requires: 'a non-zero cost in both files',
  },
  {
    id: 'trajectory',
    label: 'tool trajectory',
    klass: 'verdict',
    describe: 'A tool step was replaced or vanished. An EXTRA step is a divergence, not a regression.',
    requires: 'two or more tool calls on both sides',
  },
];

const na = (scorer: string, detail: string): ScorerResult => ({
  scorer,
  a: 'na',
  b: 'na',
  flipped: false,
  fixed: false,
  detail,
});

function result(scorer: string, a: ScoreValue, b: ScoreValue, detail: string): ScorerResult {
  return {
    scorer,
    a,
    b,
    flipped: a === 'pass' && b === 'fail',
    fixed: a === 'fail' && b === 'pass',
    detail,
  };
}

/* ── errored ─────────────────────────────────────────────────────────────── */

function erroredValue(r: Row): { v: ScoreValue; why: string } {
  if (r.error) return { v: 'fail', why: `the call returned an error: ${truncate(r.error, 140)}` };
  if (r.statusCode !== null && r.statusCode !== 200) {
    return { v: 'fail', why: `the call returned HTTP ${r.statusCode}` };
  }
  if (r.finishReason === 'length' || r.finishReason === 'max_tokens') {
    return { v: 'fail', why: 'the response was cut off at the token limit' };
  }
  if (r.outputText.trim().length === 0) return { v: 'fail', why: 'the output was empty' };
  return { v: 'pass', why: '' };
}

export function scoreErrored(a: Row, b: Row): ScorerResult {
  const ea = erroredValue(a);
  const eb = erroredValue(b);
  const detail = eb.v === 'fail' ? eb.why : ea.v === 'fail' ? `it recovered — in A, ${ea.why}` : 'both runs returned';
  return result('errored', ea.v, eb.v, detail);
}

/* ── carried assertions ──────────────────────────────────────────────────── */

const assertionKey = (c: CarriedAssertion): string => `${c.type}${c.value}${c.ordinal}`;

/**
 * Keyed on (type, value, ordinal), never on type alone.
 *
 * promptfoo routinely emits several assertions of one type on one case. With A holding
 * [contains "foo": pass, contains "bar": fail] and B holding [contains "foo": fail,
 * contains "bar": pass], a type-keyed lookup reports a pass->fail flip on the first match while
 * the true net change is zero — one fabricated regression and one hidden fix, on the same row.
 */
export function scoreCarried(a: Row, b: Row): ScorerResult {
  if (a.carried.length === 0 && b.carried.length === 0) {
    return na('carried-assertion', 'this file records no assertion results');
  }
  const mapB = new Map(b.carried.map((c) => [assertionKey(c), c]));
  const flips: string[] = [];
  const fixes: string[] = [];
  let compared = 0;
  for (const ca of a.carried) {
    const cb = mapB.get(assertionKey(ca));
    // An assertion present on one side only is added/removed, never a flip.
    if (!cb) continue;
    compared++;
    if (ca.pass && !cb.pass) flips.push(`${ca.type}${cb.reason ? `: ${truncate(cb.reason, 120)}` : ''}`);
    else if (!ca.pass && cb.pass) fixes.push(ca.type);
  }
  if (compared === 0) {
    return na('carried-assertion', 'the two runs share no assertion with the same type and value');
  }
  if (flips.length > 0) {
    return result('carried-assertion', 'pass', 'fail', `your own assertion failed — ${flips.join('; ')}`);
  }
  if (fixes.length > 0) {
    return result('carried-assertion', 'fail', 'pass', `your own assertion now passes — ${fixes.join(', ')}`);
  }
  const allPass = a.carried.every((c) => c.pass) && b.carried.every((c) => c.pass);
  return result('carried-assertion', allPass ? 'pass' : 'fail', allPass ? 'pass' : 'fail', 'no assertion changed');
}

/* ── is-json ─────────────────────────────────────────────────────────────── */

export function scoreIsJson(a: Row, b: Row): ScorerResult {
  // Our own adapters stringify an object output when the dialect stores it as JSON. That output is
  // parseable by construction, so `is-json` could never flip — and reporting a confident zero would
  // read as "nothing broke" when in fact nothing was looked at.
  if (a.outputWasSerialized || b.outputWasSerialized) {
    return na('is-json', 'this file stores the output as structured JSON, so it always parses');
  }
  const ca = classifyJson(a.outputText);
  const cb = classifyJson(b.outputText);
  // Neither run was even attempting JSON. "It stopped returning valid JSON" is not a statement
  // about this file, and reporting a confident pass or fail here would let a prose eval look as
  // though something had been checked when nothing was.
  if (ca.shape === 'prose' && cb.shape === 'prose') {
    return na('is-json', 'neither run produced JSON, so there is nothing to check');
  }
  const pa = shapePasses(ca.shape) ? 'pass' : 'fail';
  const pb = shapePasses(cb.shape) ? 'pass' : 'fail';
  if (pa === 'fail' && pb === 'fail') {
    return result('is-json', 'fail', 'fail', `neither parses (${ca.shape} → ${cb.shape})`);
  }
  const detail =
    pb === 'fail'
      ? `${cb.shape} — ${cb.reason}`
      : pa === 'fail'
        ? `it parses again (it was ${ca.shape} before)`
        : 'parses in both runs';
  return result('is-json', pa, pb, detail);
}

/* ── json-schema ─────────────────────────────────────────────────────────── */

export interface SchemaChecker {
  validate(text: string): { ok: boolean; errors: string[] } | null;
}

/**
 * `@cfworker/json-schema` rather than `ajv`, because ajv compiles validators with the `Function`
 * constructor and dies under this page's CSP.
 *
 * Two constructor arguments are load-bearing and both were missed on the first pass:
 *   - the draft, which defaults to `2019-09` and must be `2020-12`;
 *   - shortCircuit, which defaults to TRUE, so only the first error is reported. The whole promise
 *     of this tool is "with the reason attached", and one error out of five is not the reason.
 */
export function makeSchemaChecker(schemaText: string | null): SchemaChecker | { error: string } | null {
  if (!schemaText || schemaText.trim().length === 0) return null;
  let schema: unknown;
  try {
    schema = JSON.parse(schemaText);
  } catch (err) {
    return { error: `the schema itself is not valid JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  let validator: Validator;
  try {
    validator = new Validator(schema as object, '2020-12', false);
  } catch (err) {
    return { error: `the schema was rejected: ${err instanceof Error ? err.message : String(err)}` };
  }
  return {
    validate(text: string) {
      let value: unknown;
      try {
        value = JSON.parse(text);
      } catch {
        return null; // not JSON at all — is-json owns this case
      }
      try {
        const r = validator.validate(value);
        return {
          ok: r.valid,
          errors: (r.errors ?? []).map((e) => `${e.instanceLocation} ${e.error}`.trim()),
        };
      } catch (err) {
        return { ok: false, errors: [err instanceof Error ? err.message : String(err)] };
      }
    },
  };
}

export function scoreSchema(a: Row, b: Row, checker: SchemaChecker | null): ScorerResult {
  if (!checker) return na('json-schema', 'paste a JSON Schema to enable this check');
  const ra = checker.validate(a.outputText);
  const rb = checker.validate(b.outputText);
  if (!ra || !rb) return na('json-schema', 'one of the outputs is not JSON, so there is nothing to validate');
  const detail = !rb.ok
    ? rb.errors.slice(0, 4).join(' · ') || 'it does not satisfy the schema'
    : ra.ok
      ? 'satisfies the schema in both runs'
      : 'it satisfies the schema again';
  return result('json-schema', ra.ok ? 'pass' : 'fail', rb.ok ? 'pass' : 'fail', detail);
}

/* ── expected-match ──────────────────────────────────────────────────────── */

export type MatchMode = 'equals' | 'contains' | 'starts-with' | 'regex';

export function matchExpected(output: string, expected: string, mode: MatchMode): { ok: boolean; error?: string } {
  switch (mode) {
    case 'equals':
      return { ok: output.trim() === expected.trim() };
    case 'contains':
      return { ok: output.includes(expected) };
    case 'starts-with':
      return { ok: output.trimStart().startsWith(expected) };
    case 'regex': {
      // Catastrophic backtracking needs no length to bite — a 31-character pattern is enough — so
      // the input is bounded AND the pattern is compiled once, outside the hot loop, by the caller.
      if (output.length > LIMITS.maxRegexInput) {
        return { ok: false, error: `the output is longer than ${LIMITS.maxRegexInput} characters, so the pattern was not run` };
      }
      try {
        return { ok: new RegExp(expected).test(output) };
      } catch (err) {
        return { ok: false, error: `that is not a valid regular expression: ${err instanceof Error ? err.message : String(err)}` };
      }
    }
  }
}

export function scoreExpected(a: Row, b: Row, mode: MatchMode): ScorerResult {
  const expected = b.expected ?? a.expected;
  if (expected === null || expected === '') {
    return na('expected-match', 'this file has no expected / reference column');
  }
  const ma = matchExpected(a.outputText, expected, mode);
  const mb = matchExpected(b.outputText, expected, mode);
  if (ma.error || mb.error) return na('expected-match', ma.error ?? mb.error ?? '');
  const detail = mb.ok
    ? 'it matches the expected value again'
    : `it no longer ${mode === 'equals' ? 'equals' : mode === 'contains' ? 'contains' : mode === 'regex' ? 'matches' : 'starts with'} the expected value`;
  return result('expected-match', ma.ok ? 'pass' : 'fail', mb.ok ? 'pass' : 'fail', detail);
}

/* ── length bounds ───────────────────────────────────────────────────────── */

export function scoreLengthBounds(a: Row, b: Row, min: number | null, max: number | null): ScorerResult {
  if (min === null && max === null) return na('length-bounds', 'set a minimum or maximum length to enable this');
  const inside = (s: string): boolean => {
    const n = s.trim().split(/\s+/).filter(Boolean).length;
    if (min !== null && n < min) return false;
    if (max !== null && n > max) return false;
    return true;
  };
  const ia = inside(a.outputText);
  const ib = inside(b.outputText);
  const words = b.outputText.trim().split(/\s+/).filter(Boolean).length;
  return result(
    'length-bounds',
    ia ? 'pass' : 'fail',
    ib ? 'pass' : 'fail',
    ib ? `${words} words, inside the bounds` : `${words} words, outside ${min ?? '−'}–${max ?? '−'}`,
  );
}

/* ── latency / cost ──────────────────────────────────────────────────────── */

/**
 * Both numeric scorers require a non-zero A.
 *
 * `promptfoo import` writes `latencyMs: 0` on every imported row, and a cache hit writes
 * `latencyMs: 0, cost: 0`. Any ratio rule against a zero baseline is Infinity, so without this
 * guard a cached or imported run A makes 100% of rows a latency regression and the headline reads
 * "300 rows got slower" about a run where nothing was measured at all.
 */
function scoreNumeric(
  id: string,
  va: number | null,
  vb: number | null,
  cached: boolean,
  ratio: number,
  floor: number,
  fmt: (n: number) => string,
): ScorerResult {
  if (cached) return na(id, 'one of the runs was served from cache, so the figure is not comparable');
  if (va === null || vb === null) return na(id, 'this file does not record it');
  if (va <= 0) return na(id, 'the baseline run recorded zero, so there is no ratio to take');
  const worse = vb >= va * ratio && vb - va >= floor;
  const better = va >= vb * ratio && va - vb >= floor;
  if (worse) return result(id, 'pass', 'fail', `${fmt(va)} → ${fmt(vb)}`);
  if (better) return result(id, 'fail', 'pass', `${fmt(va)} → ${fmt(vb)}`);
  return result(id, 'pass', 'pass', `${fmt(va)} → ${fmt(vb)}`);
}

export function scoreLatency(a: Row, b: Row, cfg: Config): ScorerResult {
  return scoreNumeric(
    'latency-regression',
    a.latencyMs,
    b.latencyMs,
    a.cached || b.cached,
    cfg.thresholds.latencyRatio,
    cfg.thresholds.latencyFloorMs,
    (n) => `${Math.round(n)} ms`,
  );
}

export function scoreCost(a: Row, b: Row, cfg: Config): ScorerResult {
  return scoreNumeric(
    'cost-regression',
    a.cost,
    b.cost,
    a.cached || b.cached,
    cfg.thresholds.costRatio,
    cfg.thresholds.costFloor,
    (n) => `$${n.toFixed(6)}`,
  );
}

/* ── Class B — changed, not judged ───────────────────────────────────────── */

export function changeEditDistance(a: Row, b: Row): number {
  return distance(a.outputText, b.outputText);
}

function truncate(s: string, n: number): string {
  return s.length <= n ? s : `${s.slice(0, n - 1)}…`;
}
